import React, { useMemo } from 'react'
import VersatileGrid from '../../../ui_reusable/VersatileGrid'
import {
  GridCellBoldText,
  GridCellNormalTextFaded,
  GridCellSmallFadedText,
  StatsContainer,
} from '../../../ui_reusable/styles/VersatileGridUtils.styles'
import { getQualityTagByQualityString } from '../../../../utils/ScoreFitnessValidation'
import { SmallNormalTag } from '../../../ui_reusable/styles/Tags.styles'
import { HeaderTitle } from './styles/IndependentDriversList.styles'
import type { BaseSlice } from '../../comp_reusable/report_page_universal/stores/baseReportStore'
import type { ArtifactReport } from '../../../../api/generated/models/artifactReport'
import type { Dataset, DatasetQuality } from '../../../../api/generated/models'
import type { PlotGroup, PlotPackage } from '../../../../api/generated/models'

// Type for a store that implements BaseSlice
type BaseStoreHook<T extends BaseSlice> = (selector: (state: T) => any) => any

// Driver interface - extending the generated Dataset type for better type safety
interface Driver extends Dataset {
  id: number
  name: string
  quality: DatasetQuality
  updated_at: string // Ensure this is required for display purposes
  _searchId: string // Required for VersatileGrid search functionality
  _originalIndex: number // Required for VersatileGrid row tracking
}

// Props interface for the component using proper typing
interface IndependentDriversListProps {
  plotGroup?: PlotGroup
  plotPackage?: PlotPackage
  index?: number
  store?: BaseStoreHook<BaseSlice>
}

// Grid header interface - based on VersatileGrid usage patterns
interface GridHeader {
  title: string
  key: string
  type: 'string' | 'datetime' | 'number' | 'boolean'
  isSortable: boolean
  isSearchable: boolean
  flexGrow?: number
  center?: boolean
}

const IndependentDriversList: React.FC<IndependentDriversListProps> = ({
  plotGroup,
  plotPackage,
  index,
  store,
}) => {
  // Get data from the store using the proper selector pattern
  const initialReportData = store
    ? store((state: BaseSlice) => state.anyReport)
    : null

  // Extract and map independent drivers from modelData first, then fall back to initialReportData sources
  const independentDrivers = useMemo((): Driver[] => {
    // Get the available drivers data for mapping
    const availableDrivers =
      (initialReportData as ArtifactReport)?.project_solution?.drivers || []

    // Create a map for quick lookup by driver_id
    const driversMap = availableDrivers.reduce(
      (map: Record<number, Driver>, driver: any) => {
        map[driver.id] = driver as Driver
        return map
      },
      {},
    )

    // Function to map driver objects to full driver objects with proper fallback
    const mapDriverObjects = (driverObjects: readonly any[]): Driver[] => {
      return driverObjects
        .map((driverObj) => {
          // If the object already has full driver information, use it
          if (driverObj.name && driverObj.id) {
            return {
              ...driverObj,
              quality: driverObj.quality || 'good',
              updated_at: driverObj.updated_at || new Date().toISOString(),
            } as Driver
          }
          // Otherwise, try to look it up in the drivers map
          const driver = driversMap[driverObj.id]
          return driver
            ? { ...driver, quality: driver.quality || 'good' }
            : null
        })
        .filter((driver): driver is Driver => driver !== null)
    }

    // Get independent drivers from artifact.independent_drivers
    if (
      // @ts-ignore
      (initialReportData as ArtifactReport)?.artifact?.independent_drivers &&
      Array.isArray(
        // @ts-ignore
        (initialReportData as ArtifactReport).artifact!.independent_drivers,
      ) &&
      // @ts-ignore
      (initialReportData as ArtifactReport).artifact!.independent_drivers!
        .length > 0
    ) {
      const mappedDrivers = mapDriverObjects(
        // @ts-ignore
        (initialReportData as ArtifactReport).artifact!.independent_drivers!,
      )

      if (mappedDrivers.length > 0) {
        return mappedDrivers
      }
    }

    // Return empty array if no source has data
    return []
  }, [initialReportData])

  // Grid headers configuration
  const gridHeaders: GridHeader[] = [
    {
      title: 'Driver',
      key: 'name',
      type: 'string',
      isSortable: true,
      isSearchable: true,
      flexGrow: 2,
    },
    {
      title: 'Source',
      key: 'source',
      type: 'string',
      isSortable: true,
      isSearchable: true,
    },
    {
      title: 'Last Update',
      key: 'updated_at',
      type: 'datetime',
      isSortable: true,
      center: true,
      isSearchable: false,
    },
    {
      title: 'Units',
      key: 'units',
      type: 'string',
      isSortable: true,
      isSearchable: true,
      center: true,
    },
    {
      title: 'Quality',
      key: 'quality',
      type: 'string',
      isSortable: true,
      isSearchable: false,
      center: true,
    },
  ]

  // Grid row renderer - returns an array of JSX elements for each cell
  const renderGridRow = (
    rowData: any,
    rowIdx: number,
    dataIdx: number,
  ): React.ReactNode[] => {
    // Cast rowData to Driver since VersatileGrid extends the data
    const driver = rowData as Driver

    return [
      dataIdx === 0 ? (
        <GridCellNormalTextFaded key={`name-${dataIdx}`}>
          {driver.name || 'Unnamed Driver'}
        </GridCellNormalTextFaded>
      ) : (
        <GridCellBoldText key={`name-${dataIdx}`}>
          {driver.name || 'Unnamed Driver'}
        </GridCellBoldText>
      ),

      <GridCellSmallFadedText key={`source-${dataIdx}`}>
        {driver.source || 'Unknown'}
      </GridCellSmallFadedText>,

      <GridCellSmallFadedText key={`date-${dataIdx}`}>
        {driver.updated_at
          ? new Date(driver.updated_at).toISOString().slice(0, 10)
          : 'N/A'}
      </GridCellSmallFadedText>,

      <GridCellSmallFadedText key={`units-${dataIdx}`}>
        {driver.units || 'N/A'}
      </GridCellSmallFadedText>,

      <div
        key={`quality-${dataIdx}`}
        style={{ padding: '8px', display: 'flex', justifyContent: 'center' }}
      >
        {getQualityTagByQualityString(driver.quality || 'good')}
      </div>,
    ]
  }

  // Custom header with title and driver count
  const gridHeader = (
    <StatsContainer style={{ paddingTop: '0rem', paddingBottom: '0rem' }}>
      <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
        <HeaderTitle>Included drivers</HeaderTitle>
        <SmallNormalTag>
          {independentDrivers.length} drivers included in model
        </SmallNormalTag>
      </div>
    </StatsContainer>
  )

  if (!independentDrivers.length) {
    return null // Return nothing instead of showing empty state
  }

  // Gets the outcome variable driver object from the report data
  // Finds the driver that matches the outcome_variable name and returns it
  const getOutcomeVariable = (report: ArtifactReport | null): Driver | null => {
    if (!report) return null

    const outcomeVariableName = report.outcome_variable
    if (!outcomeVariableName || !report.project_solution?.drivers) return null

    const outcomeDrivers = report.project_solution.drivers.filter(
      (d: any) => d.name === outcomeVariableName,
    )
    const outcomeDriver = outcomeDrivers[0]

    if (!outcomeDriver) return null

    // Convert to Driver type with required VersatileGrid fields
    return {
      ...outcomeDriver,
      quality: outcomeDriver.quality || 'good',
      updated_at: outcomeDriver.updated_at || new Date().toISOString(),
      _searchId: `outcome_driver_${outcomeDriver.id}`,
      _originalIndex: -1, // Use -1 to indicate outcome variable
    } as Driver
  }

  // Place the outcome variable driver at the top of the table
  const outcomeVariable = getOutcomeVariable(
    initialReportData as ArtifactReport,
  )
  const rawGridData: Driver[] = outcomeVariable
    ? [outcomeVariable, ...independentDrivers]
    : independentDrivers

  // Ensure data has the required _searchId field for VersatileGrid
  const gridData = rawGridData.map((driver, index) => ({
    ...driver,
    _searchId: `driver_${driver.id}_${index}`,
    _originalIndex: index,
  }))

  return (
    <VersatileGrid
      gridContainerStyle={{
        gridTemplateColumns: '27rem 12rem 8rem 8rem 16rem',
      }}
      headers={gridHeaders}
      data={gridData}
      highlightDataIdx={0}
      renderRow={renderGridRow}
      searchPlaceholder="Search drivers..."
      headerInfo={gridHeader}
      emptyStateMessage="No drivers match your search criteria."
      paddingInGridHeader="0rem 3rem 0rem 3rem"
    />
  )
}

export default IndependentDriversList
